/**
 * Iter334 · Shared mobile bottom-sheet primitive.
 *
 * Extracted from the MultiSelectCombo mobile branch so every heavy
 * dropdown / filter / picker can adopt the same UX with a couple of
 * lines instead of reinventing portals, backdrops, body-scroll locks
 * and safe-area handling.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <BottomSheet open={open} onClose={() => setOpen(false)} title="…">
 *     <YourFilterUI />
 *   </BottomSheet>
 *
 * Behaviour:
 *   • Renders only when `open` is true AND `window.innerWidth < 768`
 *     unless `forceOnDesktop` is set. Desktop still gets the anchored
 *     variant handled by the caller.
 *   • Portals into `document.body` so no ancestor overflow/stack
 *     context can clip it.
 *   • Solid background using `var(--al-card-bg)`; backdrop uses
 *     `bg-black/50 backdrop-blur-sm` and blocks tap-through.
 *   • ESC + backdrop tap close. Body scroll is locked while open.
 *   • Respects `env(safe-area-inset-bottom)` so the "Done" button never
 *     collides with the iPhone home indicator.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < breakpoint,
  );
  useEffect(() => {
    const on = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [breakpoint]);
  return mobile;
}

export default function BottomSheet({
  open,
  onClose,
  title,
  subtitle,
  children,
  doneLabel,
  onDone,
  testid = "bottom-sheet",
  forceOnDesktop = false,
  height = "85vh",
}) {
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  if (!isMobile && !forceOnDesktop) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col justify-end"
         data-testid={testid}
         role="dialog" aria-modal="true">
      <button type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        data-testid={`${testid}-backdrop`}
      />
      <div className="relative border-t border-[var(--al-border)] rounded-t-2xl shadow-2xl flex flex-col"
           style={{
             backgroundColor: "var(--al-card-bg, #FFFFFF)",
             paddingBottom: "env(safe-area-inset-bottom, 0px)",
             maxHeight: height,
           }}
           onClick={(e) => e.stopPropagation()}>
        {(title || subtitle) && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--al-border)] shrink-0">
            <div className="min-w-0 flex-1">
              {title && (
                <div className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--al-text-muted)]">
                  {title}
                </div>
              )}
              {subtitle && (
                <div className="text-[13px] font-semibold text-[var(--al-text)] truncate">
                  {subtitle}
                </div>
              )}
            </div>
            <button type="button" onClick={onClose}
              data-testid={`${testid}-close`}
              className="w-10 h-10 rounded-full inline-flex items-center justify-center hover:bg-[var(--al-bg)] shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        <div className="overflow-y-auto flex-1 min-h-0">
          {children}
        </div>

        {(doneLabel || onDone) && (
          <div className="p-3 border-t border-[var(--al-border)] shrink-0">
            <button type="button" onClick={onDone || onClose}
              data-testid={`${testid}-done`}
              className="w-full h-11 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white text-[14px] font-bold">
              {doneLabel || "Concluir"}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
